import { create } from "zustand";
import { lookupEnglishDefinition } from "@/lib/dictionary-lookup";

export type DictionaryLanguage = "en" | "vi";

export interface DictionaryPhonetic {
    text?: string;
    audio?: string;
}

export interface DictionaryDefinition {
    definition: string;
    example?: string;
    synonyms?: string[];
    antonyms?: string[];
}

export interface DictionaryMeaning {
    partOfSpeech: string;
    definitions: DictionaryDefinition[];
}

export interface DictionaryEntry {
    word: string;
    phonetic?: string;
    phonetics: DictionaryPhonetic[];
    origin?: string;
    meanings: DictionaryMeaning[];
}

interface DictionaryState {
    isOpen: boolean;
    searchValue: string;
    language: DictionaryLanguage;
    entry: DictionaryEntry | null;
    isLoading: boolean;
    error: string | null;
    openWidget: () => void;
    closeWidget: () => void;
    toggleWidget: () => void;
    setSearchValue: (value: string) => void;
    setLanguage: (lang: DictionaryLanguage) => void;
    fetchDefinition: (word: string, lang?: DictionaryLanguage) => Promise<void>;
    clearEntry: () => void;
}

const cache = new Map<string, DictionaryEntry>();

function getCacheKey(language: DictionaryLanguage, word: string): string {
    return `${language}:${word.toLowerCase().trim()}`;
}

async function fetchVietnameseDefinition(
    word: string,
    signal: AbortSignal,
): Promise<DictionaryEntry | null> {
    try {
        const url = `https://dict.minhqnd.com/api/v1/lookup?word=${encodeURIComponent(word)}`;
        const response = await fetch(url, { signal });
        if (!response.ok) {
            if (response.status === 404 || response.status === 400) return null;
            throw new Error("Failed to fetch definition");
        }
        const data = await response.json();
        if (!data.exists || !data.results || data.results.length === 0) return null;
        const viResult = data.results.find(
            (r: { lang_code: string }) => r.lang_code === "en",
        );
        if (!viResult || !viResult.meanings || viResult.meanings.length === 0) return null;
        const meanings: DictionaryMeaning[] = viResult.meanings.map(
            (meaning: { pos: string; definition: string; example?: string }) => ({
                partOfSpeech: meaning.pos || "từ",
                definitions: [{ definition: meaning.definition, example: meaning.example }],
            }),
        );
        return { word: data.word, phonetic: undefined, phonetics: [], meanings };
    } catch (err) {
        if (err instanceof Error && err.name === "AbortError") throw err;
        return null;
    }
}

let abortController: AbortController | null = null;

export const useDictionaryStore = create<DictionaryState>()((set, get) => ({
    isOpen: false,
    searchValue: "",
    language: "en",
    entry: null,
    isLoading: false,
    error: null,

    openWidget: () => set({ isOpen: true }),
    closeWidget: () => set({ isOpen: false }),
    toggleWidget: () => set((state) => ({ isOpen: !state.isOpen })),
    setSearchValue: (value: string) => set({ searchValue: value }),
    setLanguage: (lang: DictionaryLanguage) => set({ language: lang, entry: null, error: null }),

    fetchDefinition: async (word: string, lang?: DictionaryLanguage) => {
        const targetLanguage = lang ?? get().language;
        const cleanWord = word.toLowerCase().replace(/[.,/#!$%^&*;:{}=\-_`~()'"!]/g, "").trim();
        if (!cleanWord) {
            set({ error: "Invalid word" });
            return;
        }

        const cacheKey = getCacheKey(targetLanguage, cleanWord);
        if (cache.has(cacheKey)) {
            set({ entry: cache.get(cacheKey)!, error: null });
            return;
        }

        if (abortController) abortController.abort();
        abortController = new AbortController();
        set({ isLoading: true, error: null });

        try {
            const entryData =
                targetLanguage === "en"
                    ? await lookupEnglishDefinition(cleanWord, abortController.signal)
                    : await fetchVietnameseDefinition(cleanWord, abortController.signal);

            if (entryData) {
                cache.set(cacheKey, entryData);
                set({ entry: entryData });
            } else {
                const notFoundMsg =
                    targetLanguage === "en"
                        ? `No definition found for "${word}"`
                        : `Không tìm thấy định nghĩa cho "${word}"`;
                set({ error: notFoundMsg, entry: null });
            }
        } catch (err) {
            if (err instanceof Error && err.name === "AbortError") return;
            // Surface the lookup's own reason (busy, unreachable, slow) instead
            // of flattening every failure into one generic line.
            const errorMsg =
                targetLanguage === "en"
                    ? err instanceof Error && err.message
                        ? err.message
                        : "Failed to fetch definition"
                    : "Không thể tải định nghĩa";
            set({ error: errorMsg, entry: null });
        } finally {
            set({ isLoading: false });
        }
    },

    clearEntry: () => set({ entry: null, error: null }),
}));

export const DictionaryProvider = ({ children }: { children: React.ReactNode }) => children;
